import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart } from "../context/CartContext";
import {
  FaTruck,
  FaBox,
  FaTag,
  FaShoppingCart,
  FaInfoCircle,
} from "react-icons/fa";

const ProductDetails = () => {
  const { productId } = useParams();
  const { state } = useLocation();
  const [product, setProduct] = useState(state?.product || null);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    if (!product) {
      axios
        .get(`/api/products/${productId}`)
        .then((res) => setProduct(res.data))
        .catch((err) => console.error("Failed to fetch product:", err));
    }
  }, [productId, product]);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  const hasPickup = product.shippingOption?.isPickup;

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) addToCart(product);
    navigate("/cart");
  };

  const incrementQuantity = () => setQuantity((prev) => prev + 1);
  const decrementQuantity = () => setQuantity((prev) => Math.max(1, prev - 1));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 p-6 md:p-10">
            {/* Product Image Section */}
            <div className="flex flex-col">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl transform rotate-1 group-hover:rotate-2 transition-transform duration-300"></div>
                <div className="relative bg-white rounded-2xl p-6 border border-gray-300">
                  <img
                    src={product.imageUrl || "/assets/No-img.png.png"}
                    alt={product.name}
                    className="w-full h-[400px] object-contain rounded-xl transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              </div>

              {/* Additional Info Cards */}
              <div className="grid grid-cols-3 gap-4 mt-12">
                <div className="bg-blue-100 rounded-lg p-4 text-center border border-blue-200">
                  <FaTruck className="text-blue-600 text-xl mx-auto mb-2" />
                  <p className="text-xs text-blue-800 font-medium">
                    Fast Delivery
                  </p>
                </div>
                <div className="bg-green-100 rounded-lg p-4 text-center border border-green-200">
                  <FaBox className="text-green-600 text-xl mx-auto mb-2" />
                  <p className="text-xs text-green-800 font-medium">
                    Quality Pack
                  </p>
                </div>
                <div className="bg-purple-100 rounded-lg p-4 text-center border border-purple-200">
                  <FaTag className="text-purple-600 text-xl mx-auto mb-2" />
                  <p className="text-xs text-purple-800 font-medium">
                    Best Price
                  </p>
                </div>
              </div>
            </div>

            {/* Product Info Section */}
            <div>
              <div className="mb-6">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                  {product.name}
                </h1>
                <div className="flex items-center gap-3 mb-4">
                  <span className="bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                    In Stock
                  </span>
                  <span className="text-gray-500 text-sm">
                    SKU: {product.sku || "N/A"}
                  </span>
                </div>
              </div>

              {/* Price */}
              <div className="mb-6">
                <div className="flex items-baseline gap-3">
                  <p className="text-3xl font-bold text-blue-700">
                    ${product.price}
                  </p>
                  {product.tax && (
                    <p className="text-sm text-gray-500">
                      (Incl. tax: ${product.tax.toFixed(2)})
                    </p>
                  )}
                </div>
                <p className="text-gray-500 text-sm mt-1">
                  Free shipping on orders over $50
                </p>
              </div>

              {/* Fulfillment Status */}
              {product.fulfillmentStatus && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-2">
                    <FaInfoCircle className="text-gray-600" />
                    <span className="font-medium text-gray-700">Status:</span>
                    <span className="text-gray-600">
                      {product.fulfillmentStatus.replace("_", " ")}
                    </span>
                  </div>
                </div>
              )}

              {/* Pickup Instructions */}
              {hasPickup && product.shippingOption?.pickupInstruction && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 rounded-lg">
                  <h3 className="font-bold text-yellow-800 mb-2 flex items-center gap-2">
                    <FaInfoCircle /> Pickup Information
                  </h3>
                  <div
                    className="text-sm text-yellow-700"
                    dangerouslySetInnerHTML={{
                      __html: product.shippingOption.pickupInstruction,
                    }}
                  />
                </div>
              )}

              {/* Description */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-800 mb-3">
                  Description
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  {product.description || "No product description available."}
                </p>
              </div>

              {/* Quantity Selector */}
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-800 mb-3">
                  Quantity
                </h2>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-gray-300 rounded-lg">
                    <button
                      onClick={decrementQuantity}
                      className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-l-lg transition-colors"
                    >
                      -
                    </button>
                    <span className="px-4 py-2 text-gray-800 font-medium">
                      {quantity}
                    </span>
                    <button
                      onClick={incrementQuantity}
                      className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-r-lg transition-colors"
                    >
                      +
                    </button>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {product.inventory
                      ? `${product.inventory} items available`
                      : "In stock"}
                  </p>
                </div>
              </div>

              {/* Add to Cart Button */}
              <div className="flex align-center justify-space-between gap-4">
                <button
                  onClick={handleAddToCart}
                  className="px-4 py-3 flex items-center justify-center gap-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-500 text-md text-white font-bold rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl"
                >
                  <FaShoppingCart /> Add to Cart - $
                  {(product.price * quantity).toFixed(2)}
                </button>
                <button
                  onClick={handleAddToCart}
                  className="px-4 py-3 flex items-center justify-center gap-3 bg-gradient-to-r from-[#82b016] to-green-600 hover:from-[#6fa010] hover:to-green-700 text-md text-white font-bold rounded-xl shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:shadow-xl"
                >
                  <FaShoppingCart /> Buy Now - $
                  {(product.price * quantity).toFixed(2)}
                </button>
              </div>

              {/* Product Features */}
              <div className="grid grid-cols-2 gap-4 mt-12">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">30-Day Returns</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Secure Payment</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">24/7 Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">
                    Quality Guarantee
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
